import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { ChecklistTaskData } from '../checklistData';
import { colors, contentBlockShadow } from '../../../../theme';
import { track } from '../../analytics';

const AVATAR = '/hub/checklist/fig_avatar.png';

interface NextUpCardProps {
  task: ChecklistTaskData;
  percent: number;
  done: number;
  total: number;
  onGo: () => void;
}

function NextUpCard({
  task, percent, done, total, onGo,
}: NextUpCardProps) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '12px',
        boxShadow: contentBlockShadow,
        p: { xs: '32px 24px', lg: '24px 40px' },
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 3,
        // Stacked layout: centre a 400px content column so nothing (e.g. the button)
        // stretches full-width on a wide-but-still-stacked viewport.
        alignItems: 'center',
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: 'center',
        textAlign: { xs: 'center', lg: 'left' },
        gap: { xs: 2.5, lg: 3 },
        flex: { xs: 'none', lg: 1 },
        width: { xs: '100%', lg: 'auto' },
        maxWidth: { xs: 400, lg: 'none' },
        minWidth: 0,
      }}
      >
        <Box
          component="img"
          src={AVATAR}
          alt=""
          sx={{
            width: { xs: 100, lg: 137 },
            height: { xs: 100, lg: 137 },
            borderRadius: '50%',
            flexShrink: 0,
            objectFit: 'cover',
            bgcolor: colors.secondary[50],
          }}
        />
        <Box sx={{ minWidth: 0, width: { xs: '100%', lg: 'auto' } }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.6 }}>Next Up:</Typography>
          <Typography sx={{
            fontWeight: 700, fontSize: 24, mt: '2px', lineHeight: 1.25,
          }}
          >
            {task.title}
          </Typography>
          <Typography sx={{
            fontSize: 12, color: 'text.secondary', mt: { xs: 2.5, lg: 1.5 }, lineHeight: 1.4,
          }}
          >
            {task.summary}
          </Typography>
          <Box sx={{ mt: { xs: 2.5, lg: 1.5 } }}>
            <Button
              variant="contained"
              onClick={() => {
                track('next_up_task_click', { task_name: task.title });
                onGo();
              }}
              endIcon={<ArrowDownwardIcon sx={{ fontSize: 18 }} />}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              Go To Task
            </Button>
          </Box>
        </Box>
      </Box>
      <Box sx={{
        flexShrink: 0,
        width: { xs: '100%', lg: 'auto' },
        maxWidth: { xs: 400, lg: 'none' },
        minWidth: { xs: 'auto', lg: 280 },
      }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.5,
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        }}
        >
          <Typography sx={{
            fontWeight: 700, fontSize: 30, color: colors.primary.main, lineHeight: 1.2,
          }}
          >
            {`${percent}%`}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {`Task ${done} of ${total} Complete`}
          </Typography>
        </Box>
        <Box sx={{
          height: 7, borderRadius: '4px', bgcolor: colors.primary[50], mt: 1, overflow: 'hidden',
        }}
        >
          <Box sx={{
            height: '100%', width: `${percent}%`, borderRadius: '4px', bgcolor: colors.primary.main, transition: 'width 500ms ease',
          }}
          />
        </Box>
        <Typography sx={{
          fontSize: 12, color: 'text.secondary', mt: 1, textAlign: 'left',
        }}
        >
          30-Day Checklist
        </Typography>
      </Box>
    </Box>
  );
}

export default NextUpCard;
